import { supabase } from './supabase';
import { contaDaUc, isContaSicoob, NOME_CONTA_SICOOB, type ContaSicoob } from './contaSicoob';

export type { ContaSicoob };

// Normaliza UC removendo pontos/traços/espaços/zeros à esquerda
const norm = (v: any): string =>
    (typeof v === 'string' || typeof v === 'number' ? String(v) : '').replace(/[.\-\s]/g, '').replace(/^0+/, '');

/**
 * Conta de EMISSÃO da UC (`unidades_consumidoras.conta_sicoob`) a partir do
 * uc_codigo da fatura, com o mesmo casamento de UC da emissão (BoletoEmissionModal).
 * Default: Associação quando não há UC correspondente.
 */
export async function resolveContaEmissao(ucCodigo: string): Promise<ContaSicoob> {
    if (!ucCodigo) return 'associacao';
    try {
        const { data: ucList } = await supabase
            .from('unidades_consumidoras')
            .select('conta_sicoob, emitir_em_vigor_gestao, numero_uc, outro_numero_uc')
            .or(`numero_uc.eq.${ucCodigo},outro_numero_uc.eq.${ucCodigo}`);

        if (!ucList || ucList.length === 0) return 'associacao';
        const normalizedCode = norm(ucCodigo);
        const primary = ucList.find((uc: any) => norm(uc.numero_uc) === normalizedCode || uc.numero_uc === ucCodigo);
        if (primary) return contaDaUc(primary);
        const secondary = ucList.find((uc: any) => norm(uc.outro_numero_uc) === normalizedCode || uc.outro_numero_uc === ucCodigo);
        if (secondary) return contaDaUc(secondary);
        return 'associacao';
    } catch (err) {
        console.warn('Falha ao resolver a conta Sicoob da UC', ucCodigo, err);
        return 'associacao';
    }
}

/**
 * Conta Sicoob a usar numa operação sobre um boleto JÁ EMITIDO (baixa,
 * prorrogação, consulta).
 *
 * Prefere sempre `faturas.conta_sicoob`, que é carimbado na emissão a partir da
 * linha digitável — ou seja, é a conta em que o boleto realmente existe. Só cai
 * na dedução pela UC quando a fatura é legada e não tem o campo.
 *
 * Deduzir é a origem do bug do "Boleto não encontrado" (5002): uma cobrança
 * avulsa saía na Associação enquanto a UC apontava Gestão, e a baixa ia bater na
 * conta errada. Pior: cada conta tem sequência própria de nossoNumero, e o mesmo
 * número existe em mais de uma apontando para boletos diferentes — deduzir
 * errado pode dar baixa na cobrança de outro cliente.
 */
export async function resolveContaSicoob(
    fatura: { id?: number | string | null; conta_sicoob?: string | null; uc_codigo?: string | null }
): Promise<ContaSicoob> {
    if (isContaSicoob(fatura.conta_sicoob)) return fatura.conta_sicoob;

    // O campo pode simplesmente não ter vindo no select que montou a tela. Antes de
    // cair na dedução (que é o que causava a baixa na conta errada), buscar direto.
    if (fatura.id != null) {
        try {
            const { data } = await supabase
                .from('faturas')
                .select('conta_sicoob')
                .eq('id', fatura.id)
                .maybeSingle();
            if (isContaSicoob(data?.conta_sicoob)) return data.conta_sicoob;
        } catch (err) {
            console.warn('Falha ao buscar conta_sicoob da fatura', fatura.id, err);
        }
    }

    // Legado sem linha digitável reconhecível: só aqui a UC decide.
    return resolveContaEmissao(fatura.uc_codigo || '');
}

export interface ContaSicoobInfo {
    conta: ContaSicoob;
    nome: string;
    /** false = a conta ainda não pode ser escolhida (o n8n não roteia ela). */
    emissao_ativa: boolean;
}

/** As contas de `contas_sicoob`, na ordem da tela. */
export async function fetchContasSicoob(): Promise<ContaSicoobInfo[]> {
    const { data, error } = await supabase
        .from('contas_sicoob')
        .select('conta, nome, emissao_ativa')
        .order('ordem');
    if (error) throw error;
    return (data || []).filter((c: any) => isContaSicoob(c.conta)) as ContaSicoobInfo[];
}

/**
 * Trava da emissão: a conta precisa estar liberada em `contas_sicoob`.
 *
 * A HSolar entrou desligada porque o fluxo de emissão do n8n ainda não conhece a
 * conta — um pedido para ela sairia na Associação, sem erro nenhum. O banco já
 * impede escolher conta desligada na UC; isto cobre a UC que já estava nela
 * quando a conta foi desligada e o agrupamento.
 */
export async function exigirContaLiberada(conta: ContaSicoob): Promise<void> {
    const { data, error } = await supabase
        .from('contas_sicoob')
        .select('nome, emissao_ativa')
        .eq('conta', conta)
        .maybeSingle();
    const nome = data?.nome || NOME_CONTA_SICOOB[conta];
    if (error) throw new Error(`Não foi possível confirmar se a conta ${nome} pode emitir: ${error.message}`);
    if (data?.emissao_ativa !== true) {
        throw new Error(`A conta ${nome} ainda não está liberada para emitir boletos. Troque a conta Sicoob da UC (ou do agrupamento) antes de emitir.`);
    }
}

/** Conjunto de chaves de UC (normalizadas) marcadas para Vigor Gestão. */
export async function fetchGestaoUcKeys(): Promise<Set<string>> {
    const keys = new Set<string>();
    const { data } = await supabase
        .from('unidades_consumidoras')
        .select('numero_uc, outro_numero_uc')
        .eq('conta_sicoob', 'gestao');
    (data || []).forEach((uc: any) => {
        const k1 = norm(uc.numero_uc); if (k1) keys.add(k1);
        const k2 = norm(uc.outro_numero_uc); if (k2) keys.add(k2);
    });
    return keys;
}

/** True se a fatura (pelo uc_codigo) pertence a uma UC da Vigor Gestão. */
export function isGestaoFatura(ucCodigo: any, gestaoKeys: Set<string>): boolean {
    const k = norm(ucCodigo);
    return !!k && gestaoKeys.has(k);
}
